import { useContext, useEffect, useState } from "react"
import { EpisodesContext } from "../../controllers/episodes"
import EpisodeSlide from "../../models/episodeSlide"
import SlideConstraints from "../../models/slideConstraints"
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import {
  checkTextColorOnBoxBackColorChange,
  onSlideAuthorTextFieldChange,
  onSlideAuthorTitleTextFieldChange,
  onSlideBackgroundChange,
  onSlideBoxBackColorTextFieldChange,
  onSlideDecorChange,
  onSlideImageChange,
  onSlideTextColorTextFieldChange,
} from "../../services/utilities/episodeUtility"
import {
  capitalizeFirstCharacter,
  deepCopy,
} from "../../services/utilities/utility"
import HtmlEditor from "../global/htmlEditor"
import BigPreview from "../episode/bigPreview"
import ImagePicker from "../global/imagePicker"
import { MediaSize } from "../../services/config/enum"
import {
  boxBackColors,
  textColors,
} from "../../services/config/colorsForSlides"
import { Slide } from "@aworld/app-preview"

const QuoteSlide = ({
  slide,
  language,
  index,
  constraints,
  changesCancelled,
}: {
  slide: EpisodeSlide
  language: string
  index: number
  constraints: SlideConstraints
  changesCancelled: boolean
}) => {
  const {
    currentEpisode,
    setCurrentEpisode,
    slidesErrors,
    setSlidesErrors,
    editMode,
  } = useContext(EpisodesContext)

  // errors
  const [textError, setTextError] = useState<boolean>(false)
  const [textErrorType, setTextErrorType] = useState<string>("")
  const [authorError, setAuthorError] = useState<boolean>(false)
  const [authorErrorType, setAuthorErrorType] = useState<string>("")
  const [authorTitleError, setAuthorTitleError] = useState<boolean>(false)
  const [authorTitleErrorType, setAuthorTitleErrorType] = useState<string>("")
  const [imageError, setImageError] = useState<boolean>(false)

  // check errors
  const [isFirstRender, setisFirstRender] = useState<boolean>(true)

  useEffect(() => {
    if (isFirstRender) {
      setisFirstRender(false)

      // first errors check
      if (
        slide.text.replace(/(<([^>]+)>)/gi, "").length >
        constraints.text.maxSize
      ) {
        setTextError(true)
        setTextErrorType(
          "Maximum length is " + constraints.text.maxSize + " characters"
        )
      }
      if (
        slide.text.replace(/(<([^>]+)>)/gi, "").length <
        constraints.text.minSize
      ) {
        setTextError(true)
        setTextErrorType(
          "Minimum length is " + constraints.text.minSize + " characters"
        )
      }
      if (slide.author.length > constraints.author.maxSize) {
        setAuthorError(true)
        setAuthorErrorType(
          "Maximum length is " + constraints.author.maxSize + " characters"
        )
      }
      if (slide.author.length < constraints.author.minSize) {
        setAuthorError(true)
        setAuthorErrorType(
          "Minimum length is " + constraints.author.minSize + " characters"
        )
      }
      if (slide.authorTitle.length > constraints.authorTitle.maxSize) {
        setAuthorTitleError(true)
        setAuthorTitleErrorType(
          "Maximum length is " + constraints.authorTitle.maxSize + " characters"
        )
      }
      if (slide.authorTitle.length < constraints.authorTitle.minSize) {
        setAuthorTitleError(true)
        setAuthorTitleErrorType(
          "Minimum length is " + constraints.authorTitle.minSize + " characters"
        )
      }
      if (!slide.image.url.length) {
        setImageError(true)
      }

      return
    }

    let newSlidesErrors: {
      lang: string
      slides: { slideId: string; error: boolean }[]
    }[] = deepCopy(slidesErrors)

    if (!textError && !authorError && !authorTitleError && !imageError) {
      newSlidesErrors
        .find((item) => item.lang === language)
        .slides.find((item) => item.slideId === slide.id).error = false
      setSlidesErrors(newSlidesErrors)
    } else {
      newSlidesErrors
        .find((item) => item.lang === language)
        .slides.find((item) => item.slideId === slide.id).error = true
      setSlidesErrors(newSlidesErrors)
    }
  }, [textError, authorError, authorTitleError, imageError])

  // changes cancelled
  useEffect(() => {
    if (changesCancelled) {
      setTextError(false)
      setTextErrorType("")
      setAuthorError(false)
      setAuthorErrorType("")
      setAuthorTitleError(false)
      setAuthorTitleErrorType("")
      setImageError(false)
    }
  }, [changesCancelled])

  // big preview
  const [isBigPreviewOpen, setIsBigPreviewOpen] = useState<boolean>(false)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={8}>
        <Stack spacing={2}>
          <Typography variant="h6" className="card-subtitle">
            Text
          </Typography>
          <HtmlEditor
            disabled={!editMode}
            slide={slide}
            constraints={constraints}
            currentEpisode={currentEpisode}
            setCurrentEpisode={setCurrentEpisode}
            slideLanguage={language}
            index={index}
            error={textError}
            setError={setTextError}
            errorType={textErrorType}
            setErrorType={setTextErrorType}
            changesCancelled={changesCancelled}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              disabled={!editMode}
              value={slide.author ? slide.author : ""}
              fullWidth
              label="Author"
              variant="outlined"
              size="small"
              error={authorError}
              helperText={authorErrorType}
              onChange={(e: any) => {
                onSlideAuthorTextFieldChange(
                  currentEpisode,
                  setCurrentEpisode,
                  e,
                  language,
                  slide,
                  index
                )
                if (e.target.value.length > constraints.author.maxSize) {
                  setAuthorError(true)
                  setAuthorErrorType(
                    "Maximum length is " +
                      constraints.author.maxSize +
                      " characters"
                  )
                } else if (e.target.value.length < constraints.author.minSize) {
                  setAuthorError(true)
                  setAuthorErrorType(
                    "Minimum length is " +
                      constraints.author.minSize +
                      " characters"
                  )
                } else {
                  setAuthorError(false)
                  setAuthorErrorType("")
                }
              }}
            />
            <TextField
              disabled={!editMode}
              value={slide.authorTitle ? slide.authorTitle : ""}
              fullWidth
              label="Author title"
              variant="outlined"
              size="small"
              error={authorTitleError}
              helperText={authorTitleErrorType}
              onChange={(e: any) => {
                onSlideAuthorTitleTextFieldChange(
                  currentEpisode,
                  setCurrentEpisode,
                  e,
                  language,
                  slide,
                  index
                )
                if (e.target.value.length > constraints.authorTitle.maxSize) {
                  setAuthorTitleError(true)
                  setAuthorTitleErrorType(
                    "Maximum length is " +
                      constraints.authorTitle.maxSize +
                      " characters"
                  )
                } else if (
                  e.target.value.length < constraints.authorTitle.minSize
                ) {
                  setAuthorTitleError(true)
                  setAuthorTitleErrorType(
                    "Minimum length is " +
                      constraints.authorTitle.minSize +
                      " characters"
                  )
                } else {
                  setAuthorTitleError(false)
                  setAuthorTitleErrorType("")
                }
              }}
            />
          </Stack>
          <FormControl fullWidth disabled={!editMode}>
            <InputLabel id="text-color-label-quoteSlide" size="small">
              Text color
            </InputLabel>
            <Select
              size="small"
              labelId="text-color-label-quoteSlide"
              value={slide.textColor ? slide.textColor : ""}
              label="Text color"
              onChange={(e: any) => {
                onSlideTextColorTextFieldChange(
                  currentEpisode,
                  setCurrentEpisode,
                  e.target.value,
                  language,
                  slide,
                  index
                )
              }}
            >
              {textColors.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="h6" className="card-subtitle">
            Background
          </Typography>
          <Stack direction="row" spacing={2}>
            <ImagePicker
              filters={{
                category: "background",
              }}
              disabled={!editMode}
              title="Background"
              pickedImage={slide.background ? slide.background : ""}
              setPickedImage={(selectedImage: {
                url: string
                id: string
                description?: string
              }) => {
                onSlideBackgroundChange(
                  currentEpisode,
                  setCurrentEpisode,
                  selectedImage,
                  language,
                  slide,
                  index
                )
              }}
              width="50%"
            />
            <Stack spacing={2} style={{ width: "50%" }}>
              <FormControl fullWidth disabled>
                <InputLabel id="box-shape-label-quoteSlide" size="small">
                  Box shape
                </InputLabel>
                <Select
                  size="small"
                  labelId="box-shape-label-quoteSlide"
                  value="squared"
                  label="Box shape"
                >
                  {constraints.box.boxShape.map((item) => (
                    <MenuItem value={item} key={item}>
                      {capitalizeFirstCharacter(item)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!editMode}>
                <InputLabel id="box-back-color-label-quoteSlide" size="small">
                  Box back color
                </InputLabel>
                <Select
                  size="small"
                  labelId="box-back-color-label-quoteSlide"
                  value={
                    slide.box && slide.box.boxBackColor
                      ? slide.box.boxBackColor
                      : "none"
                  }
                  label="Box back color"
                  onChange={(e: any) => {
                    if (e.target.value === "none") {
                      onSlideBoxBackColorTextFieldChange(
                        currentEpisode,
                        setCurrentEpisode,
                        null,
                        language,
                        slide,
                        index
                      )
                    } else {
                      onSlideBoxBackColorTextFieldChange(
                        currentEpisode,
                        setCurrentEpisode,
                        e.target.value,
                        language,
                        slide,
                        index
                      )
                    }

                    checkTextColorOnBoxBackColorChange(
                      currentEpisode,
                      setCurrentEpisode,
                      e,
                      language,
                      slide,
                      index
                    )
                  }}
                >
                  <MenuItem value="none">None</MenuItem>
                  {boxBackColors.map((item) => (
                    <MenuItem value={item.value} key={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
          <Typography variant="h6" className="card-subtitle">
            Images
          </Typography>
          <Stack direction="row" spacing={2}>
            <ImagePicker
              filters={{
                category: "decor",
              }}
              disabled={!editMode}
              title="Decor"
              removeButton
              pickedImage={slide.decor ? slide.decor : ""}
              setPickedImage={(selectedImage: {
                url: string
                id: string
                description?: string
              }) => {
                onSlideDecorChange(
                  currentEpisode,
                  setCurrentEpisode,
                  selectedImage,
                  language,
                  slide,
                  index
                )
              }}
              width="50%"
            />
            <ImagePicker
              error={imageError}
              filters={{
                category: "content",
                size: [MediaSize.xs],
              }}
              disabled={!editMode}
              title="Image"
              pickedImage={
                slide.image && slide.image.url ? slide.image.url : ""
              }
              setPickedImage={(selectedImage: {
                url: string
                id: string
                description?: string
                type?: MediaSize
              }) => {
                onSlideImageChange(
                  currentEpisode,
                  setCurrentEpisode,
                  selectedImage,
                  language,
                  slide,
                  index
                )
                setImageError(false)
              }}
              width="50%"
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={12} md={4}>
        <Stack spacing={2}>
          <Typography variant="h6" className="card-subtitle">
            Preview
          </Typography>
          <div
            style={{
              zoom: 0.6,
              marginTop: 30,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              maxHeight: "100%",
              cursor: "pointer",
              opacity: isBigPreviewOpen ? 0 : 1,
              transition: "200ms",
            }}
            onClick={() => {
              setIsBigPreviewOpen(true)
            }}
          >
            <Slide
              slide={slide}
              constraints={constraints}
              className="preview"
              primaryTopicName={
                currentEpisode.topics.find((topic) => topic.primary)
                  ? currentEpisode.topics.find((topic) => topic.primary).topic
                      .name
                  : null
              }
              episodeTitle={
                currentEpisode.translations.find(
                  (item) => item.lang === language
                ).title ?? null
              }
              numberOfSlides={
                currentEpisode.translations.find(
                  (item) => item.lang === language
                ).slides.length
              }
              numberOfQuiz={
                currentEpisode.translations.find(
                  (item) => item.lang === language
                ).quiz.length
              }
              currentSlide={index}
            />
          </div>
        </Stack>
      </Grid>
      <BigPreview
        isBigPreviewOpen={isBigPreviewOpen}
        setIsBigPreviewOpen={setIsBigPreviewOpen}
      >
        <div
          style={{
            zoom: window.innerHeight / 950,
            marginTop: 70,
          }}
        >
          <Slide
            slide={slide}
            constraints={constraints}
            className="preview"
            primaryTopicName={
              currentEpisode.topics.find((topic) => topic.primary)
                ? currentEpisode.topics.find((topic) => topic.primary).topic
                    .name
                : null
            }
            episodeTitle={
              currentEpisode.translations.find((item) => item.lang === language)
                .title ?? null
            }
            numberOfSlides={
              currentEpisode.translations.find((item) => item.lang === language)
                .slides.length
            }
            numberOfQuiz={
              currentEpisode.translations.find((item) => item.lang === language)
                .quiz.length
            }
            currentSlide={index}
          />
        </div>
      </BigPreview>
    </Grid>
  )
}

export default QuoteSlide
